import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import ChallengeCard from './components/ChallengeCard';
import ChallengeLeaderboard from './components/ChallengeLeaderboard';
import ProgressTracker from './components/ProgressTracker';
import CreateChallengeModal from './components/CreateChallengeModal';

const ChallengeSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading, isGymOwner, isTrainer } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const tabs = [
    { id: 'active', label: 'Active Challenges', icon: 'Zap' },
    { id: 'upcoming', label: 'Upcoming', icon: 'Calendar' },
    { id: 'completed', label: 'Completed', icon: 'CheckCircle' },
    { id: 'my-challenges', label: 'My Challenges', icon: 'User' }
  ];

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadChallenges();
      loadUserProgress();
    }
  }, [user, currentTenant, activeTab]);

  const loadChallenges = async () => {
    setLoading(true);
    try {
      let query = supabase
        ?.from('challenges')
        ?.select(`
          *,
          creator:user_profiles!challenges_created_by_fkey(id, full_name, avatar_url),
          participants:user_challenge_progress(count)
        `)
        ?.eq('tenant_id', currentTenant?.id);

      const now = new Date()?.toISOString();

      if (activeTab === 'active') {
        query = query?.lte('start_date', now)?.gte('end_date', now);
      } else if (activeTab === 'upcoming') {
        query = query?.gt('start_date', now);
      } else if (activeTab === 'completed') {
        query = query?.lt('end_date', now);
      } else if (activeTab === 'my-challenges') {
        const { data: myProgress } = await supabase
          ?.from('user_challenge_progress')
          ?.select('challenge_id')
          ?.eq('user_id', user?.id);
        
        const challengeIds = myProgress?.map(p => p?.challenge_id) || [];
        if (challengeIds?.length === 0) {
          setChallenges([]);
          setLoading(false);
          return;
        }
        query = query?.in('id', challengeIds);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      setChallenges(data || []);
    } catch (error) {
      console.error('Error loading challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = async () => {
    try {
      const { data, error } = await supabase
        ?.from('user_challenge_progress')
        ?.select('*')
        ?.eq('user_id', user?.id);

      if (error) throw error;

      const progressMap = {};
      data?.forEach(progress => {
        progressMap[progress?.challenge_id] = progress;
      });
      setUserProgress(progressMap);
    } catch (error) {
      console.error('Error loading user progress:', error);
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    try {
      const { error } = await supabase
        ?.from('user_challenge_progress')
        ?.insert({
          challenge_id: challengeId,
          user_id: user?.id,
          tenant_id: currentTenant?.id,
          current_value: 0,
          is_completed: false
        });

      if (error) throw error;

      await loadChallenges();
      await loadUserProgress();
    } catch (error) {
      console.error('Error joining challenge:', error);
    }
  };

  const handleLeaveChallenge = async (challengeId) => {
    try {
      const { error } = await supabase
        ?.from('user_challenge_progress')
        ?.delete()
        ?.eq('challenge_id', challengeId)
        ?.eq('user_id', user?.id);

      if (error) throw error;

      await loadChallenges();
      await loadUserProgress();
    } catch (error) {
      console.error('Error leaving challenge:', error);
    }
  };

  const handleCreateChallenge = async (challengeData) => {
    try {
      const { error } = await supabase
        ?.from('challenges')
        ?.insert({
          ...challengeData,
          tenant_id: currentTenant?.id,
          created_by: user?.id
        });

      if (error) throw error;

      setShowCreateModal(false);
      await loadChallenges();
    } catch (error) {
      console.error('Error creating challenge:', error);
    }
  };

  const canCreateChallenge = isGymOwner || isTrainer;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 overflow-y-auto pb-20 lg:pb-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                  Challenge System
                </h1>
                <p className="text-muted-foreground">
                  Compete, achieve, and celebrate your fitness milestones
                </p>
              </div>
              {canCreateChallenge && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium"
                >
                  <Icon name="Plus" size={20} />
                  Create Challenge
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-border overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-medium transition-smooth border-b-2 ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Icon name="Loader2" size={48} className="animate-spin text-primary" />
            </div>
          ) : challenges?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Icon name="Trophy" size={64} color="var(--color-muted-foreground)" className="mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No challenges found
              </h3>
              <p className="text-muted-foreground mb-6">
                {activeTab === 'my-challenges' ?'Join a challenge to start tracking your progress' :'Check back later for new challenges'}
              </p>
              {canCreateChallenge && activeTab === 'active' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium"
                >
                  Create First Challenge
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {challenges?.map((challenge) => (
                <ChallengeCard
                  key={challenge?.id}
                  challenge={challenge}
                  userProgress={userProgress?.[challenge?.id]}
                  onJoin={() => handleJoinChallenge(challenge?.id)}
                  onLeave={() => handleLeaveChallenge(challenge?.id)}
                  onViewDetails={() => setSelectedChallenge(challenge)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Challenge Details Modal */}
      {selectedChallenge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-2xl font-heading font-bold text-foreground">
                {selectedChallenge?.title}
              </h2>
              <button
                onClick={() => setSelectedChallenge(null)}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6">
              <ChallengeLeaderboard
                challengeId={selectedChallenge?.id}
                tenantId={currentTenant?.id}
                currentUserId={user?.id}
              />
              {userProgress?.[selectedChallenge?.id] && (
                <div className="mt-6">
                  <ProgressTracker
                    challenge={selectedChallenge}
                    progress={userProgress?.[selectedChallenge?.id]}
                    onUpdate={loadUserProgress}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Challenge Modal */}
      {showCreateModal && (
        <CreateChallengeModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateChallenge}
        />
      )}

      <MobileBottomNav />
    </div>
  );
};

export default ChallengeSystem;